import { useState } from 'react';
import type { Person, Suggestion } from '@ambit/shared';
import { api } from '../api.ts';

/**
 * The bar to suggest something is deliberately low: one required field. The
 * whole point is that a kid can fire off "that trampoline place in Hagerstown"
 * without knowing the price, the hours, or where it actually is — working that
 * out is what the research step is for.
 */
export function SuggestBox({
  people,
  me,
  onAdded,
}: {
  people: Person[];
  me: string | null;
  onAdded: (suggestion: Suggestion) => void;
}) {
  const [open, setOpen] = useState(false);
  const [what, setWhat] = useState('');
  const [why, setWhy] = useState('');
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [thanks, setThanks] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await api.createSuggestion({
        what,
        why: why || null,
        url: url || null,
        suggestedBy: me,
      });
      onAdded(created);
      setWhat('');
      setWhy('');
      setUrl('');
      setOpen(false);
      setThanks(`Added "${created.what}". We'll look into it.`);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div className="mt-4">
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            setThanks(null);
          }}
          className="rounded-md bg-spruce-800 px-4 py-2.5 text-sm text-bone-300 transition-colors hover:border-blaze-400 hover:text-bone-100"
        >
          + Suggest something
        </button>
        {thanks && (
          <p role="status" className="mt-2 text-sm text-blaze-300">
            {thanks}
          </p>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="mt-4 rounded-md bg-spruce-800 p-4">
      <label htmlFor="what" className="display block text-[10px] uppercase tracking-widest text-bone-500">
        What is it?
      </label>
      <input
        id="what"
        autoFocus
        required
        value={what}
        onChange={(e) => setWhat(e.target.value)}
        placeholder="that trampoline place in Hagerstown"
        className="mt-1.5 w-full rounded-md bg-spruce-950 px-3 py-2 text-bone-100 placeholder:text-bone-500"
      />

      <label htmlFor="why" className="display mt-3 block text-[10px] uppercase tracking-widest text-bone-500">
        Why? <span className="normal-case tracking-normal text-bone-500">optional</span>
      </label>
      <textarea
        id="why"
        rows={2}
        value={why}
        onChange={(e) => setWhy(e.target.value)}
        placeholder="Maddie went for a birthday and said it was great"
        className="mt-1.5 w-full resize-y rounded-md bg-spruce-950 px-3 py-2 text-bone-100 placeholder:text-bone-500"
      />

      <label htmlFor="url" className="display mt-3 block text-[10px] uppercase tracking-widest text-bone-500">
        Link <span className="normal-case tracking-normal text-bone-500">optional</span>
      </label>
      <input
        id="url"
        type="url"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://…"
        className="mt-1.5 w-full rounded-md bg-spruce-950 px-3 py-2 text-bone-100 placeholder:text-bone-500"
      />

      {!me && people.length > 0 && (
        <p className="mt-3 text-xs text-blaze-300">
          Pick who you are at the bottom of the page so we know whose idea this was.
        </p>
      )}
      {error && (
        <p role="alert" className="mt-3 text-sm text-blaze-400">
          {error}
        </p>
      )}

      <div className="mt-4 flex gap-2">
        <button
          type="submit"
          disabled={busy || what.trim().length < 2}
          className="rounded-sm bg-blaze-500 px-4 py-2 text-sm font-semibold text-spruce-950 hover:bg-blaze-400 disabled:opacity-50"
        >
          {busy ? 'Adding…' : 'Add it'}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded-md bg-spruce-700 px-4 py-2 text-sm text-bone-300 transition-colors hover:bg-spruce-600 hover:text-bone-100"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

const STATE_LABEL: Record<Suggestion['state'], string> = {
  new: 'waiting',
  researching: 'looking into it',
  accepted: 'in the catalog',
  declined: 'not this time',
};

export function SuggestionList({
  suggestions,
  onDeclined,
}: {
  suggestions: Suggestion[];
  onDeclined?: (updated: Suggestion) => void;
}) {
  if (suggestions.length === 0) return null;

  return (
    <ul className="mt-4 space-y-2">
      {suggestions.map((s) => (
        <li key={s.id} className="rounded-md bg-spruce-800 px-3 py-2.5">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm text-bone-100">{s.what}</span>
            <span
              className={`display shrink-0 text-[10px] uppercase tracking-widest ${
                s.state === 'accepted'
                  ? 'text-blaze-400'
                  : s.state === 'declined'
                    ? 'text-bone-500'
                    : 'text-bone-500'
              }`}
            >
              {STATE_LABEL[s.state]}
            </span>
          </div>
          {s.why && <p className="mt-1 text-xs text-bone-500">{s.why}</p>}
          {s.note && <p className="mt-1 text-xs italic text-blaze-300">{s.note}</p>}
          {s.suggestedByName && (
            <p className="mt-1 text-xs text-bone-500">{s.suggestedByName}&rsquo;s idea</p>
          )}
          {/*
            The queue was write-only from here: add one, watch it sit. The
            decline route has always existed and nothing in the UI called it,
            so the only way out was a database or an MCP client. A test entry
            from the day the catalog was built is still in there.
          */}
          {onDeclined && s.state !== 'accepted' && s.state !== 'declined' && (
            <Decline id={s.id} onDeclined={onDeclined} />
          )}
        </li>
      ))}
    </ul>
  );
}

/**
 * Declining takes a reason because the server requires one, and it is right to.
 * A suggestion that disappears without a note gets suggested again next month;
 * "drove out, it was shut on Mondays" is the thing worth keeping.
 */
function Decline({ id, onDeclined }: { id: string; onDeclined: (s: Suggestion) => void }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-1.5 text-xs text-bone-500 underline decoration-dotted underline-offset-4 transition-colors hover:text-blaze-300"
      >
        not this one
      </button>
    );
  }

  const submit = () => {
    const reason = note.trim();
    if (!reason || busy) return;
    setBusy(true);
    api
      .declineSuggestion(id, reason)
      .then(onDeclined)
      .catch(() => undefined)
      .finally(() => setBusy(false));
  };

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
      <input
        autoFocus
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
          if (e.key === 'Escape') setOpen(false);
        }}
        placeholder="Why not?"
        aria-label="Why this one is not happening"
        className="min-w-0 flex-1 rounded-md bg-spruce-950 px-2.5 py-1.5 text-xs text-bone-100"
      />
      <button
        type="button"
        onClick={submit}
        disabled={busy || note.trim() === ''}
        className="rounded-md bg-spruce-600 px-2.5 py-1.5 text-xs text-bone-100 transition-colors hover:bg-spruce-500 disabled:opacity-50"
      >
        Decline
      </button>
    </div>
  );
}
